import { useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Turnstile } from '@marsidev/react-turnstile';
import { useTranslation } from 'react-i18next';
import { HiExclamationTriangle, HiShieldExclamation } from 'react-icons/hi2';
import { FaGoogleDrive } from 'react-icons/fa';
import { TbBrandOnedrive } from 'react-icons/tb';
import {
  useValidateUploadTokenQuery,
  useUploadViaLinkMutation,
  useGetPublicImportConfigQuery,
} from '@/store/publicUploadApi';
import { useToast } from '@/hooks/useToast';
import { usePublicGooglePicker } from '@/hooks/usePublicGooglePicker';
import { usePublicOneDrivePicker } from '@/hooks/usePublicOneDrivePicker';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import FileUploadZone from '@/components/documents/FileUploadZone';
import {
  UploadUsageInfo,
  UploadUsageBarTrack,
  UploadUsageBarFill,
} from '@/components/documents/DocumentUpload.styled';
import {
  PageWrapper,
  PageContainer,
  HeroHeader,
  TenantName,
  SubInfo,
  MessageBox,
  TurnstileWrapper,
  ButtonRow,
  ActionButton,
  UsageSection,
  UploadCounter,
  ErrorContainer,
  ErrorIcon,
  ErrorTitle,
  ErrorDescription,
  LoadingContainer,
} from './PublicUploadPage.styled';

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
}

export default function PublicUploadPage() {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation();
  const { showSuccess, showError } = useToast();

  const {
    data: tokenInfo,
    isLoading,
    isError,
    error,
  } = useValidateUploadTokenQuery(token ?? '', { skip: !token });

  const { data: importConfig } = useGetPublicImportConfigQuery(token ?? '', { skip: !token });

  const [uploadViaLink] = useUploadViaLinkMutation();
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileSolved, setTurnstileSolved] = useState(false);
  const [uploadCount, setUploadCount] = useState(0);

  const { openPicker: openGooglePicker, isConfigured: googleConfigured } =
    usePublicGooglePicker(importConfig ?? null);
  const { openPicker: openOneDrivePicker, isConfigured: oneDriveConfigured } =
    usePublicOneDrivePicker(importConfig ?? null);

  const remainingBytes = tokenInfo?.remainingBytes ?? 0;
  const isUploadDisabled = !turnstileToken || remainingBytes <= 0;
  const usagePercent =
    tokenInfo && tokenInfo.maxSizeBytes > 0
      ? (tokenInfo.totalUploadedBytes / tokenInfo.maxSizeBytes) * 100
      : 0;

  const handleUploadFile = useCallback(
    async (file: File) => {
      if (!token || !turnstileToken) throw new Error('Not ready');

      if (file.size > remainingBytes) {
        showError(t('customerUploadLink.public.fileTooLarge'));
        throw new Error('File too large');
      }

      await uploadViaLink({
        token,
        fileName: file.name,
        turnstileToken,
        file,
      }).unwrap();

      setUploadCount((c) => c + 1);
      showSuccess(t('customerUploadLink.public.uploadSuccess'));
    },
    [token, turnstileToken, remainingBytes, uploadViaLink, showSuccess, showError, t],
  );

  const handleGoogleDrive = useCallback(async () => {
    if (!token || !turnstileToken) return;

    try {
      const result = await openGooglePicker();
      if (!result) return;

      showSuccess(
        t('customerUploadLink.public.googleDriveStarted', {
          count: result.fileIds.length + result.folderIds.length,
        }),
      );
    } catch {
      showError(t('customerUploadLink.public.googleDriveError'));
    }
  }, [token, turnstileToken, openGooglePicker, showSuccess, showError, t]);

  const handleOneDrive = useCallback(async () => {
    if (!token || !turnstileToken) return;

    try {
      const result = await openOneDrivePicker();
      if (!result) return;

      showSuccess(
        t('customerUploadLink.public.oneDriveStarted', {
          count: result.items.length,
        }),
      );
    } catch {
      showError(t('customerUploadLink.public.oneDriveError'));
    }
  }, [token, turnstileToken, openOneDrivePicker, showSuccess, showError, t]);

  // Loading
  if (isLoading) {
    return (
      <LoadingContainer>
        <LoadingSpinner size="lg" />
      </LoadingContainer>
    );
  }

  // Error / Invalid token
  if (isError || !tokenInfo) {
    const is404 = error && 'status' in error && error.status === 404;
    const is410 = error && 'status' in error && error.status === 410;

    return (
      <ErrorContainer>
        <ErrorIcon>
          {is410 ? <HiExclamationTriangle /> : <HiShieldExclamation />}
        </ErrorIcon>
        <ErrorTitle>
          {is410
            ? t('customerUploadLink.public.expired')
            : is404
              ? t('customerUploadLink.public.notFound')
              : t('customerUploadLink.public.invalidLink')}
        </ErrorTitle>
        <ErrorDescription>
          {is410
            ? t('customerUploadLink.public.expiredDescription')
            : is404
              ? t('customerUploadLink.public.notFoundDescription')
              : t('customerUploadLink.public.invalidLinkDescription')}
        </ErrorDescription>
      </ErrorContainer>
    );
  }

  // Valid token — upload page
  const cloudButtons = (
    <ButtonRow>
      {googleConfigured && (
        <ActionButton
          $variant="google"
          onClick={handleGoogleDrive}
          disabled={isUploadDisabled}
        >
          <FaGoogleDrive />
          {t('customerUploadLink.public.googleDrive')}
        </ActionButton>
      )}
      {oneDriveConfigured && (
        <ActionButton
          $variant="onedrive"
          onClick={handleOneDrive}
          disabled={isUploadDisabled}
        >
          <TbBrandOnedrive />
          {t('customerUploadLink.public.oneDrive')}
        </ActionButton>
      )}
    </ButtonRow>
  );

  return (
    <PageWrapper>
      <PageContainer>
        <HeroHeader>
          <TenantName>{tokenInfo.tenantName}</TenantName>
          {tokenInfo.customerName && (
            <SubInfo>
              {t('customerUploadLink.public.uploadFor', { customer: tokenInfo.customerName })}
            </SubInfo>
          )}
          {tokenInfo.sprawaSignature && (
            <SubInfo>
              {t('customerUploadLink.public.case', { signature: tokenInfo.sprawaSignature })}
            </SubInfo>
          )}
        </HeroHeader>

        {tokenInfo.message && <MessageBox>{tokenInfo.message}</MessageBox>}

        <TurnstileWrapper>
          {!turnstileSolved && (
            <Turnstile
              siteKey={tokenInfo.turnstileSiteKey}
              onSuccess={(token) => {
                setTurnstileSolved(true);
                setTurnstileToken(token);
              }}
            />
          )}
        </TurnstileWrapper>

        <FileUploadZone
          onUploadFile={handleUploadFile}
          disabled={isUploadDisabled}
          extraButtons={cloudButtons}
          i18nPrefix="customerUploadLink.public"
        />

        <UsageSection>
          <UploadUsageInfo>
            <span>
              {t('customerUploadLink.public.sizeUsage', {
                used: formatBytes(tokenInfo.totalUploadedBytes),
                total: formatBytes(tokenInfo.maxSizeBytes),
              })}
            </span>
          </UploadUsageInfo>
          <UploadUsageBarTrack>
            <UploadUsageBarFill $percent={usagePercent} $warning={usagePercent >= 80} />
          </UploadUsageBarTrack>
        </UsageSection>

        {uploadCount > 0 && (
          <UploadCounter>
            {t('customerUploadLink.public.filesUploaded', { count: uploadCount })}
          </UploadCounter>
        )}
      </PageContainer>
    </PageWrapper>
  );
}
